/**
 * API endpoint configuration for the Auction Chase platform.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3200';
const WS_BASE_URL = process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3200';

export const API_ENDPOINTS = {
  API_BASE_URL,
  WS_BASE_URL,
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    REGISTER: `${API_BASE_URL}/auth/register`,
    VERIFY_EMAIL: `${API_BASE_URL}/auth/verify-email`,
    ME: `${API_BASE_URL}/auth/me`,
    FORGOT_PASSWORD: `${API_BASE_URL}/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/reset-password`,
    GOOGLE_CODE_SIGNIN: `${API_BASE_URL}/auth/google/code-signin`,
  },
  USERS: {
    BASE: `${API_BASE_URL}/users`,
    PROFILE: `${API_BASE_URL}/users/profile`,
    UPDATE_PROFILE: (userId: string) =>
      `${API_BASE_URL}/users/${userId}/profile`,
    UPDATE_PASSWORD: (userId: string) =>
      `${API_BASE_URL}/users/${userId}/password`,
    ROLES: `${API_BASE_URL}/users/roles`,
    PERMISSIONS: `${API_BASE_URL}/users/permissions`,
    ACTIVITY: (userId: string) => `${API_BASE_URL}/users/${userId}/activity`,
  },
  VISITORS: {
    BASE: `${API_BASE_URL}/visitors`,
    TRACK: `${API_BASE_URL}/visitors/track`,
    JOURNEY: `${API_BASE_URL}/visitors/journey`,
    JOURNEY_BATCH: `${API_BASE_URL}/visitors/journey/batch`,
    UPDATE: (visitorId: string) => `${API_BASE_URL}/visitors/${visitorId}`,
    SESSION: (visitorId: string) =>
      `${API_BASE_URL}/visitors/${visitorId}/session`,
  },
  TOURNAMENTS: {
    BASE: `${API_BASE_URL}/tournaments`,
    ACTIVE: `${API_BASE_URL}/tournaments/active`,
    PUBLIC: `${API_BASE_URL}/tournaments/public`,
    SITEMAP: `${API_BASE_URL}/tournaments/sitemap`,
    PROFILE: (id: string) => `${API_BASE_URL}/tournaments/${id}`,
    START: (id: string) => `${API_BASE_URL}/tournaments/${id}/start`,
    END: (id: string) => `${API_BASE_URL}/tournaments/${id}/end`,
    ENROLLMENT: (id: string) => `${API_BASE_URL}/tournaments/${id}/enrollment`,
    ENROLLMENTS: (id: string) =>
      `${API_BASE_URL}/tournaments/${id}/enrollments`,
    APPROVE_ENROLLMENT: (tournamentId: string, enrollmentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/enrollments/${enrollmentId}/approve`,
    REJECT_ENROLLMENT: (tournamentId: string, enrollmentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/enrollments/${enrollmentId}/reject`,
    GENERATE_PLAYER_REPORTS: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/players/generate-pdf`,
  },
  TEAMS: {
    BASE: `${API_BASE_URL}/teams`,
    MY_TEAM: `${API_BASE_URL}/teams/my-team`,
    SITEMAP: `${API_BASE_URL}/teams/sitemap`,
    PROFILE: (id: string) => `${API_BASE_URL}/teams/${id}`,
    PLAYERS: (teamId: string) => `${API_BASE_URL}/teams/${teamId}/players`,
    UPLOAD_LOGO: (teamId: string) => `${API_BASE_URL}/teams/${teamId}/logo`,
  },
  PLAYERS: {
    BASE: `${API_BASE_URL}/players`,
    SITEMAP: `${API_BASE_URL}/players/sitemap`,
    PROFILE: (id: string) => `${API_BASE_URL}/players/${id}`,
    UPLOAD_IMAGE: (id: string) => `${API_BASE_URL}/players/${id}/image`,
    TOURNAMENT: (id: string, searchParams: string) =>
      `${API_BASE_URL}/players/tournament/${id}?${searchParams}`,
    SEARCH: (searchParams: string) =>
      `${API_BASE_URL}/players/search?${searchParams}`,
    BULK_UPLOAD: `${API_BASE_URL}/players/bulk-upload`,
    ICON: (id: string) => `${API_BASE_URL}/players/${id}/icon`,
    TEAM: (id: string) => `${API_BASE_URL}/players/${id}/team`,
    REQUEST_ENROLLMENT: `${API_BASE_URL}/players/request-enrollment`,
    ENROLLMENTS: `${API_BASE_URL}/players/enrollments`,
    APPROVE_ENROLLMENT: (enrollmentId: string) =>
      `${API_BASE_URL}/players/enrollment/${enrollmentId}/approve`,
    REJECT_ENROLLMENT: (enrollmentId: string) =>
      `${API_BASE_URL}/players/enrollment/${enrollmentId}/reject`,
  },
  AUCTIONS: {
    BASE: `${API_BASE_URL}/auctions`,
    PROFILE: (id: string) => `${API_BASE_URL}/auctions/${id}`,
    START: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/start`,
    BID: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/bid`,
    APPROVE: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/approve`,
    OPT_OUT: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/opt-out`,
    PRESALE: `${API_BASE_URL}/auctions/presale`,
    REVERT_PRESALE: `${API_BASE_URL}/auctions/revert-presale`,
  },
  POOLS: {
    BASE: `${API_BASE_URL}/pools`,
    PROFILE: (id: string) => `${API_BASE_URL}/pools/${id}`,
    PLAYERS: (poolId: string) => `${API_BASE_URL}/pools/${poolId}/players`,
    RULES: (poolId: string) => `${API_BASE_URL}/pools/${poolId}/rules`,
    RANDOM_PLAYER: (poolId: string) =>
      `${API_BASE_URL}/pools/${poolId}/random-player`,
  },
  ENROLLMENTS: {
    BASE: `${API_BASE_URL}/enrollments`,
    TOURNAMENT: (tournamentId: string) =>
      `${API_BASE_URL}/enrollments/tournament/${tournamentId}`,
  },
  UPLOADS: {
    BASE: `${API_BASE_URL}/uploads`,
  },
  PUBLIC: {},
  SUPPORT: {
    SEND: `${API_BASE_URL}/support`,
  },
  MARKETING: {
    BASE: `${API_BASE_URL}/marketing`,
    UNSUBSCRIBE: `${API_BASE_URL}/marketing/unsubscribe`,
  },
} as const;

export { API_BASE_URL, WS_BASE_URL };
